import { useEffect, useState } from 'react';
import { Redirect, Route, useLocation } from 'react-router-dom';
import { IonApp, IonRouterOutlet, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Capacitor } from '@capacitor/core';
import { initAndroidPushRegistration } from './utils/capPushNotifications';
import { Preferences } from '@capacitor/preferences';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/**
 * Ionic Dark Palette
 * -----------------------------------------------------
 * For more information, please see:
 * https://ionicframework.com/docs/theming/dark-mode
 */

//import '@ionic/react/css/palettes/dark.always.css';
// import '@ionic/react/css/palettes/dark.class.css';
// import '@ionic/react/css/palettes/dark.system.css';

/* Theme variables */
import './theme/variables.css';

/* Google Fonts */
import '@fontsource/plus-jakarta-sans/300.css';
import '@fontsource/plus-jakarta-sans/400.css';
import '@fontsource/plus-jakarta-sans/500.css';
import '@fontsource/plus-jakarta-sans/700.css';

import { AppProvider } from './AppProvider';
import { TimerProvider } from "./TimerProvider";
import Connection from './pages/Connection';
import Onboarding from './pages/Onboarding';
import Home from './pages/Home';
import About from './pages/About';
import FAQ from './pages/FAQ';
import Login from './pages/Login';
import ForgotPasscode from './pages/ForgotPasscode';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TCUPolicy from './pages/TCUPolicy';
import Register from './pages/Register';
import Welcome from './pages/Welcome';
import Menu from './pages/Menu';
import {SoloWiseLoadingOverlay } from './components/SoloWiseLoadingOverlay';

setupIonicReact({
    mode: 'md',
});

declare global { interface Window { gtag?: (...args: any[]) => void; } }

const GA4RouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const gtag = window.gtag;
    if (!gtag) return;

    const page_path = location.pathname + location.search + location.hash;
    console.log(page_path);
    gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path,
      // debug_mode: true  // uncomment while testing DebugView
    });
  }, [location]);

  return null;
}

/*const theme = createTheme({
  // you can override typography here if you want,
  // but MUI defaults to Roboto once it's loaded.
  palette: {
    //mode: 'dark',
    primary: { main: '#0054e9', }, // your brand
    info: { main: '#9AC8FB', }, 
  },
  typography: {
    fontFamily: 'Source Sans Pro, Roboto, Helvetica, Arial, sans-serif',
    // Override headings to use Encode Sans SC first
    h1: { fontFamily: '"Source Sans Pro", "Roboto", sans-serif' },
    h2: { fontFamily: '"Source Sans Pro", "Roboto", sans-serif' },
    h3: { fontFamily: '"Source Sans Pro", "Roboto", sans-serif' },
    h4: { fontFamily: '"Source Sans Pro", "Roboto", sans-serif' },
    h5: { fontFamily: '"Source Sans Pro", "Roboto", sans-serif' },
    h6: { fontFamily: '"Source Sans Pro", "Roboto", sans-serif' },
  },
});*/

const App: React.FC = () => {
  const [showLoader, setShowLoader] = useState<boolean>(true);

  useEffect(() => {
    const effect = async () => {
      const theme = await getTheme();
      if (theme == 1) document.body.classList.add('dark');
      else document.body.classList.remove('dark');
    };

    effect();
  }, []);


  const getTheme = async () => {
    const theme: any = await Preferences.get({key: 'theme'}); 
    return theme.value !== null ? theme : 0;
  }

  return (
    <AppProvider showLoader={showLoader} setShowLoader={setShowLoader}>  
      {/*<ThemeProvider theme={theme}>
      <CssBaseline />*/}
        <IonApp>
          <SoloWiseLoadingOverlay open={showLoader} label='SoloWise' />
          <TimerProvider>
            <IonReactRouter>
              <IonRouterOutlet>
                <GA4RouteTracker />
                <Route exact path="/">
                  <Home />
                </Route>
                <Route component={Connection} path="/connection" exact /> 
                <Route component={Onboarding} path="/onboarding" exact />                
                <Route component={About} path="/about" exact />
                <Route component={FAQ} path="/faq" exact />
                <Route component={Login} path="/login" exact />
                <Route component={ForgotPasscode} path="/login/forgot-passcode" exact />
                <Route component={PrivacyPolicy} path="/privacy-policy" exact />
                <Route component={TCUPolicy} path="/terms-conditions-policy" exact />
                <Route component={Register} path="/register" exact  />
                <Route component={Welcome} path="/welcome/:token" exact />
                <Route component={Menu} path="/account" />
                <Route path={'/welcome'} exact>
                    <Redirect to='/login' />
                </Route>     
              </IonRouterOutlet>
            </IonReactRouter>
          </TimerProvider>
        </IonApp>
        {/*</ThemeProvider>*/}
      </AppProvider>
    );
};

export default App;
